import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../partage/api/client';
import type { DossierVu } from '../partage/api/types';
import { refusDeCreation } from '../partage/creation';
import { useAllerALaFiche } from '../fiche';
import { cles } from '../partage/relectures';
import { useCreer } from '../partage/useArbre';
import { SupprimerDossier } from './SupprimerDossier';
import { useDeuxGestes } from '../partage/deuxGestes';
import { rangerLesProjets, type TriDesProjets } from '../partage/lesProjets';
import type { ArriveeSurLaFeuille } from '../partage/navigationDeLaFeuille';
import { OUVRIR_LA_SAISIE_DU_PROJET } from '../partage/saisieDuProjet';
import { useAuPouce, useCouche } from '../partage/surface';
import { useMenuOuvert } from '../partage/useMenuOuvert';
import { IconePlus } from '../partage/icones';
import {
  autresActionsPour,
  continuerDans,
  DEUX_GESTES,
  MES_PROJETS,
  NOUVEAU_PROJET,
  RANGER_LES_PROJETS,
  RAYON_EN_COURS,
  RAYON_FINIS,
  SUPPRIMER_LE_PROJET,
  TRIS_DES_PROJETS,
} from '../partage/vocabulaire';
import { couleurDAxe } from '../carte';
import { infosDuCarnet } from './carnet';
import { NouveauProjet } from './NouveauProjet';
import { ProjetsVides } from './ProjetsVides';
import { TousLesProjets } from './TousLesProjets';
import { teinteDuProjet, teinteOuAccent } from '../apparence';
import './Dossiers.css';

/**
 * Le tri choisi tient le temps de la session : on revient d'un projet sur le même ordre.
 * « Par échéance » à l'ouverture.
 */
let triRetenu: TriDesProjets = 'echeance';

/**
 * « Projets » à l'ordinateur : **une étagère de carnets** (chantier etagere-des-projets,
 * décisions 19 et 20 du carnet, maquette du carnet `projets.html`).
 *
 * En haut, « Mes projets » et les trois tris ; dessous, les trois chemins d'un nouveau projet ;
 * puis le rayon « En cours », un carnet par projet non fini et, au bout, la carte « Nouveau
 * projet » ; puis le rayon « Finis », les projets Acquis en étiquettes barrées. Un clic entre
 * dans un projet, un double-clic ouvre sa fiche ; « … » garde « Supprimer le projet ».
 *
 * Le serveur calcule (la prochaine chose, la date, les comptes, l'habit, les branches) ; la
 * page range et dessine.
 *
 * Au téléphone (`useAuPouce`), Tous les projets prend sa place (`TousLesProjets.tsx`) ; la page
 * sans projet reste la même partout.
 */
export function Dossiers() {
  const naviguer = useNavigate();
  const allerALaFiche = useAllerALaFiche();
  const projets = useQuery({ queryKey: cles.projets, queryFn: api.projets });
  const creer = useCreer(null);
  const [tri, setTri] = useState<TriDesProjets>(triRetenu);
  const auPouce = useAuPouce();
  // Couché, les carnets de face (chantier telephone-couche-suite, 02), jamais l'étagère de l'ordinateur.
  const couche = useCouche();
  const [aSupprimer, setASupprimer] = useState<DossierVu | null>(null);

  if (projets.isPending) return <div className="dossiers" />;

  const liste = projets.data?.dossiers ?? [];
  /*
   * Sans projet à soi (ticket 14), la page vide qui présente « Maintenant ». Le voyage de
   * démo d'une partie de l'Aide ne compte pas : il est dans la liste, pas à l'utilisateur.
   */
  if (projets.data?.sansProjet || liste.length === 0) {
    return <ProjetsVides onOuvrir={(titre) => creer.mutate(titre)} refus={refusDeCreation(creer.error)} />;
  }

  // Au téléphone, Tous les projets : une tuile par projet (chantier `telephone-projets-fiche`, ticket 01).
  if (auPouce || couche) return <TousLesProjets dossiers={liste} couche={couche} />;

  function ranger(choix: TriDesProjets) {
    triRetenu = choix;
    setTri(choix);
  }

  const rangee = rangerLesProjets(liste, tri);
  const enCours = rangee.filter((d) => !d.fini);
  const finis = rangee.filter((d) => d.fini);

  const gestesDe = (dossier: DossierVu) => ({
    onEntrer: () => naviguer(`/feuille/${dossier.id}`),
    onFiche: () =>
      naviguer(`/feuille/${dossier.id}`, { state: { ouvrirLaFiche: dossier.id } satisfies ArriveeSurLaFeuille }),
    onSupprimer: () => setASupprimer(dossier),
  });

  return (
    <div className="dossiers">
      <header className="dossiers-tete">
        <h1>{MES_PROJETS}</h1>
        <div className="dossiers-tris" role="group" aria-label={RANGER_LES_PROJETS}>
          {TRIS_DES_PROJETS.map(([choix, mot]) => (
            <button
              key={choix}
              type="button"
              className="dossiers-tri"
              aria-pressed={tri === choix}
              onClick={() => ranger(choix)}
            >
              {mot}
            </button>
          ))}
        </div>
      </header>

      {/* Les trois chemins d'un nouveau projet (`NouveauProjet`). */}
      <NouveauProjet />

      <p className="dossiers-rayon">{RAYON_EN_COURS}</p>
      <div className="dossiers-etagere">
        {enCours.map((dossier) => (
          <Carnet
            key={dossier.id}
            dossier={dossier}
            {...gestesDe(dossier)}
            // « Continuer » : la fiche de la prochaine chose, ouverte dans sa feuille.
            onContinuer={(id) => void allerALaFiche(id)}
          />
        ))}
        {/* La carte « Nouveau projet » ouvre la saisie du nom, comme la barre de gauche. */}
        <button
          type="button"
          className="dossiers-carnet-neuf"
          onClick={() => naviguer('/dossiers', { state: OUVRIR_LA_SAISIE_DU_PROJET })}
        >
          <IconePlus className="dossiers-carnet-neuf-icone" />
          {NOUVEAU_PROJET}
        </button>
      </div>

      {finis.length > 0 && (
        <>
          <p className="dossiers-rayon">{RAYON_FINIS}</p>
          <div className="dossiers-finis">
            {finis.map((dossier) => (
              <ProjetFini key={dossier.id} dossier={dossier} {...gestesDe(dossier)} />
            ))}
          </div>
        </>
      )}

      {aSupprimer && <SupprimerDossier dossier={aSupprimer} onFermer={() => setASupprimer(null)} />}
    </div>
  );
}

interface GestesDuProjet {
  dossier: DossierVu;
  onEntrer: () => void;
  onFiche: () => void;
  onSupprimer: () => void;
}

/**
 * Le carnet d'un projet. Toute sa couverture est un seul bouton (`dossier-carte`, posé dessous) :
 * un projet est une bulle, il a les deux gestes de toutes les bulles (un clic entre, un
 * double-clic ouvre sa fiche). Par-dessus, ce qui se lit ne prend aucun clic ; seuls le titre
 * de « Prochaine » (« Continuer ») et « … » sont des gestes à part : un bouton niché dans un
 * bouton se déclencherait avec lui.
 *
 * Sur la tranche, un intercalaire par branche, dans l'ordre de ses axes sur la carte et à la
 * même couleur (`axe`, dit par le serveur : la forme, elle, arrive dans l'ordre du chargement) ; pâli quand la branche est finie, cerclé d'ocre
 * quand elle attend le monde. En bas à droite, un galet de la forme des bulles du projet.
 */
function Carnet({ dossier, onEntrer, onFiche, onSupprimer, onContinuer }: GestesDuProjet & { onContinuer: (id: string) => void }) {
  const gestes = useDeuxGestes(onEntrer, onFiche);
  const infos = infosDuCarnet(dossier);
  const { suite, fin } = infos;

  return (
    <div
      className="carnet"
      data-fond={dossier.habit.fond}
      data-tuto="dossier"
      data-dossier={dossier.id}
      style={{ ['--teinte' as string]: teinteDuProjet(dossier.couleur) }}
    >
      <button
        type="button"
        className="carnet-ouvrir"
        data-tuto="dossier-carte"
        aria-label={dossier.titre}
        title={DEUX_GESTES}
        {...gestes}
      />

      <span className="carnet-onglets" aria-hidden="true">
        {[...dossier.forme].sort((a, b) => a.axe - b.axe).map((branche) => (
          <span
            key={branche.id}
            className="carnet-onglet"
            data-finie={branche.finie ? 'oui' : undefined}
            data-attend={branche.attendLeMonde ? 'oui' : undefined}
            style={{
              // La couleur choisie de la branche, sinon celle de son axe (chantier habit-des-bulles, 03).
              ['--axe' as string]: branche.couleur
                ? teinteOuAccent(branche.couleur)
                : couleurDAxe({ [branche.id]: branche.axe }, branche.id),
            }}
          />
        ))}
      </span>

      {/* La forme des bulles du projet, dessinée par les feuilles des formes (`styles/bulles/*.css`). */}
      <span className="carnet-galet forme-de-projet" data-forme={dossier.habit.forme} aria-hidden="true">
        <span className="bulle-d-apercu" data-forme={dossier.habit.forme} />
      </span>

      <span className="carnet-etiquette">
        <b data-tuto="dossier-titre">{dossier.titre}</b>
      </span>

      <span className="carnet-infos">
        <span className="carnet-acquis" data-tuto="dossier-acquis">
          <span>{infos.faites}</span>
          <span className="carnet-jauge" aria-hidden="true">
            <i style={{ width: `${infos.part * 100}%` }} />
          </span>
        </span>
        {suite?.sorte === 'chose' && (
          <span className="carnet-ligne" data-tuto={suite.continuer ? 'dossier-maintenant' : undefined}>
            {suite.etiquette} :{' '}
            {suite.continuer ? (
              <button
                type="button"
                className="carnet-continuer"
                title={continuerDans(suite.titre)}
                onClick={() => onContinuer(suite.continuer!)}
              >
                {suite.titre}
              </button>
            ) : (
              <b>{suite.titre}</b>
            )}
          </span>
        )}
        {suite?.sorte === 'phrase' && <span className="carnet-ligne">{suite.phrase}</span>}
        <span
          className={fin.monde ? 'carnet-ligne carnet-ligne--monde' : 'carnet-ligne'}
          data-tuto={fin.monde ? undefined : 'dossier-tot'}
        >
          {fin.phrase}
        </span>
      </span>

      <MenuDuProjet titre={dossier.titre} onSupprimer={onSupprimer} classe="dossiers-menu--carnet" />
    </div>
  );
}

/** Un projet fini, au rayon « Finis » : son nom barré, les deux gestes, et « … ». */
function ProjetFini({ dossier, onEntrer, onFiche, onSupprimer }: GestesDuProjet) {
  const gestes = useDeuxGestes(onEntrer, onFiche);
  return (
    <span className="dossiers-fini" data-tuto="dossier" data-dossier={dossier.id}>
      <button type="button" className="dossiers-fini-nom" data-tuto="dossier-carte" title={DEUX_GESTES} {...gestes}>
        <span data-tuto="dossier-titre">{dossier.titre}</span>
      </button>
      <MenuDuProjet titre={dossier.titre} onSupprimer={onSupprimer} classe="dossiers-menu--fini" />
    </span>
  );
}

/**
 * « … » sur un projet : un petit menu, toujours visible, jamais au survol (interdit n° 1).
 * Aujourd'hui une seule action, « Supprimer le projet », qui ouvre la confirmation. Échap ou un
 * clic ailleurs le referme. Le tuto entoure « … », puis, menu ouvert, « Supprimer le projet ».
 */
function MenuDuProjet({ titre, onSupprimer, classe }: { titre: string; onSupprimer: () => void; classe: string }) {
  const { ouvert, setOuvert, ref: zone } = useMenuOuvert<HTMLSpanElement>();

  return (
    <span className={`dossiers-menu ${classe}`} ref={zone}>
      <button
        type="button"
        className="dossiers-plus-d-actions"
        data-tuto={ouvert ? undefined : 'dossier-supprimer'}
        aria-label={autresActionsPour(titre)}
        aria-haspopup="menu"
        aria-expanded={ouvert}
        onClick={() => setOuvert((o) => !o)}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="5" cy="12" r="1.8" />
          <circle cx="12" cy="12" r="1.8" />
          <circle cx="19" cy="12" r="1.8" />
        </svg>
      </button>
      {ouvert && (
        <span className="dossiers-menu-liste" role="menu">
          <button
            type="button"
            role="menuitem"
            className="dossiers-menu-choix"
            data-tuto="dossier-supprimer"
            autoFocus
            onClick={() => {
              setOuvert(false);
              onSupprimer();
            }}
          >
            {SUPPRIMER_LE_PROJET}
          </button>
        </span>
      )}
    </span>
  );
}
